// src/utils/api/subscriptionAPI.ts
import axios from "axios";
import { BASE_API_URL } from "../consts";

// Helper to get clientId from localStorage
const getClientId = () => {
  try {
    const user = localStorage.getItem("user");
    if (user) {
      const parsedUser = JSON.parse(user);
      return parsedUser.clientId;
    }
  } catch (error) {
    console.error("Failed to get clientId from localStorage:", error);
  }
  return null;
};

// Create axios instance with auth headers
const createAxiosInstance = () => {
  const token = localStorage.getItem("token");
  const clientId = getClientId();

  const headers: any = {
    "Content-Type": "application/json",
    "x-access-token": token || "",
  };

  // Add clientId header if available (for non-auth testing)
  if (clientId) {
    headers["x-client-id"] = clientId;
  }

  return axios.create({
    baseURL: BASE_API_URL,
    headers,
  });
};

export const subscriptionAPI = {
  // Client-facing APIs
  getMySubscription: async () => {
    const api = createAxiosInstance();
    return await api.get("/subscription/me");
  },

  getQuickStatus: async () => {
    const api = createAxiosInstance();
    return await api.get("/subscription/status");
  },

  getMyBillingPeriods: async (params?: {
    status?: string;
    year?: number;
    limit?: number;
    skip?: number;
  }) => {
    const api = createAxiosInstance();
    return await api.get("/subscription/periods", { params });
  },

  getBillingPeriodDetails: async (periodId: string) => {
    const api = createAxiosInstance();
    return await api.get(`/subscription/periods/${periodId}`);
  },

  getMyPaymentHistory: async (params?: {
    startDate?: string;
    endDate?: string;
    method?: string;
    limit?: number;
  }) => {
    const api = createAxiosInstance();
    return await api.get("/subscription/payment-history", { params });
  },

  downloadInvoice: async (periodId: string) => {
    const api = createAxiosInstance();
    return await api.get(`/subscription/invoice/${periodId}`);
  },
};

export const subscriptionAdminAPI = {
  // Plan Management
  getAllPlans: async (params?: { isActive?: boolean }) => {
    const api = createAxiosInstance();
    return await api.get("/admin/plans", { params });
  },

  getPlanById: async (planId: string) => {
    const api = createAxiosInstance();
    return await api.get(`/admin/plans/${planId}`);
  },

  createPlan: async (data: any) => {
    const api = createAxiosInstance();
    return await api.post("/admin/plans", data);
  },

  updatePlan: async (planId: string, data: any) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/plans/${planId}`, data);
  },

  deactivatePlan: async (planId: string) => {
    const api = createAxiosInstance();
    return await api.delete(`/admin/plans/${planId}`);
  },

  deletePlan: async (planId: string) => {
    const api = createAxiosInstance();
    return await api.delete(`/admin/plans/${planId}/hard`);
  },

  // Subscription Management
  getAllSubscriptions: async (params?: {
    status?: string;
    planId?: string;
    isAccessBlocked?: boolean;
    limit?: number;
    skip?: number;
  }) => {
    const api = createAxiosInstance();
    return await api.get("/admin/subscriptions", { params });
  },

  getSubscriptionById: async (subscriptionId: string) => {
    const api = createAxiosInstance();
    return await api.get(`/admin/subscriptions/${subscriptionId}`);
  },

  createSubscription: async (data: {
    clientId: string;
    planId: string;
    startDate?: string;
    customMonthlyPrice?: number;
    customPricePerEmployee?: number;
    discount?: {
      percentage: number;
      reason: string;
      expiresAt?: string;
    };
    customPaymentTermDays?: number;
    customGracePeriodDays?: number;
    notes?: string;
  }) => {
    const api = createAxiosInstance();
    return await api.post("/admin/subscriptions", data);
  },

  updateSubscription: async (subscriptionId: string, data: any) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/subscriptions/${subscriptionId}`, data);
  },

  deleteSubscription: async (subscriptionId: string) => {
    const api = createAxiosInstance();
    return await api.delete(`/admin/subscriptions/${subscriptionId}`);
  },

  pauseSubscription: async (subscriptionId: string) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/subscriptions/${subscriptionId}`, {
      action: "pause",
    });
  },

  resumeSubscription: async (subscriptionId: string) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/subscriptions/${subscriptionId}`, {
      action: "resume",
    });
  },

  cancelSubscription: async (subscriptionId: string, cancelReason: string) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/subscriptions/${subscriptionId}`, {
      action: "cancel",
      cancelReason,
    });
  },

  blockAccess: async (subscriptionId: string, blockReason: string) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/subscriptions/${subscriptionId}`, {
      action: "blockAccess",
      blockReason,
    });
  },

  unblockAccess: async (subscriptionId: string) => {
    const api = createAxiosInstance();
    return await api.patch(`/admin/subscriptions/${subscriptionId}`, {
      action: "unblockAccess",
    });
  },

  changePlan: async (
    subscriptionId: string,
    newPlanId: string,
    prorated: boolean = true
  ) => {
    const api = createAxiosInstance();
    return await api.post(
      `/admin/subscriptions/${subscriptionId}/change-plan`,
      {
        newPlanId,
        prorated,
      }
    );
  },

  regenerateBillingPeriods: async (subscriptionId: string) => {
    const api = createAxiosInstance();
    return await api.post(
      `/admin/subscriptions/${subscriptionId}/regenerate-periods`
    );
  },

  // Billing Period Management
  getClientBillingPeriods: async (
    clientId: string,
    params?: {
      status?: string;
      year?: number;
      limit?: number;
      skip?: number;
    }
  ) => {
    const api = createAxiosInstance();
    return await api.get(`/admin/billing/${clientId}`, { params });
  },

  markBillingPeriodPaid: async (
    billingPeriodId: string,
    data: {
      amount?: number;
      method?: string;
      paidAt?: string;
      reference?: string;
      note?: string;
      bankDetails?: any;
    }
  ) => {
    const api = createAxiosInstance();
    return await api.post(`/admin/billing/${billingPeriodId}/mark-paid`, data);
  },

  waiveBillingPeriod: async (billingPeriodId: string, reason: string) => {
    const api = createAxiosInstance();
    return await api.post(`/admin/billing/${billingPeriodId}/waive`, {
      reason,
    });
  },

  addNoteToBillingPeriod: async (billingPeriodId: string, note: string) => {
    const api = createAxiosInstance();
    return await api.post(`/admin/billing/${billingPeriodId}/add-note`, {
      note,
    });
  },

  generateBillingPeriods: async (
    subscriptionId: string,
    monthsAhead: number = 3,
    force: boolean = false
  ) => {
    const api = createAxiosInstance();
    return await api.post(`/admin/billing/${subscriptionId}/generate-periods`, {
      monthsAhead,
      force,
    });
  },

  // Payment Management
  getAllPayments: async (params?: {
    clientId?: string;
    startDate?: string;
    endDate?: string;
    method?: string;
    status?: string;
    limit?: number;
    skip?: number;
  }) => {
    const api = createAxiosInstance();
    return await api.get("/admin/payments", { params });
  },

  getPaymentById: async (paymentId: string) => {
    const api = createAxiosInstance();
    return await api.get(`/admin/payments/${paymentId}`);
  },

  refundPayment: async (
    paymentId: string,
    amount?: number,
    reason?: string
  ) => {
    const api = createAxiosInstance();
    return await api.post(`/admin/payments/${paymentId}/refund`, {
      amount,
      reason,
    });
  },

  // System
  checkOverduePeriods: async () => {
    const api = createAxiosInstance();
    return await api.post("/admin/billing/check-overdue");
  },

  getDashboardStats: async () => {
    const api = createAxiosInstance();
    return await api.get("/admin/billing/dashboard");
  },
};

const subscriptionAPIs = { subscriptionAPI, subscriptionAdminAPI };
export default subscriptionAPIs;
